import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Tooltip,
  useCallbackRef,
  useMixpanel,
} from '@talos/kyoko';
import {
  multilegComboSliceActions,
  selectCustomLegs,
  selectInstrumentField,
  selectIsEntryMode,
  selectIsLoading,
  selectOptionStrategy,
} from './MultilegComboSlice';
import { isMultilegOptionComboType } from './enums';

export function MultilegFlipLegsButton({ panelID }: { panelID: string }) {
  const dispatch = useAppStateDispatch();
  const mixpanel = useMixpanel();
  const isEntryMode = useAppStateSelector(state => selectIsEntryMode(state, panelID));
  const isLoading = useAppStateSelector(state => selectIsLoading(state, panelID));
  const customLegs = useAppStateSelector(state => selectCustomLegs(state, panelID));
  const optionStrategy = useAppStateSelector(state => selectOptionStrategy(state, panelID));
  const instrumentField = useAppStateSelector(state => selectInstrumentField(state, panelID));

  const canSwapLegs =
    isEntryMode &&
    ((isMultilegOptionComboType(instrumentField?.value?.value) && (optionStrategy?.legs?.length ?? 0) >= 2) ||
      !!(customLegs && customLegs.legs.length >= 2));

  const handleSwapLegs = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.FlipMultilegLegs, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.MultilegComboScreen,
      ...(instrumentField?.value?.value != null
        ? { [MixpanelEventProperty.Type]: String(instrumentField.value.value) }
        : {}),
    });
    if (isMultilegOptionComboType(instrumentField?.value?.value) && optionStrategy && optionStrategy.legs.length >= 2) {
      dispatch(multilegComboSliceActions.updateOptionStrategy({ panelID, optionStrategy: optionStrategy.swapLegs() }));
    } else if (customLegs && customLegs.legs.length >= 2) {
      dispatch(multilegComboSliceActions.updateCustomLegs({ panelID, customLegs: customLegs.swapLegs() }));
    }
  });

  if (!canSwapLegs) {
    return null;
  }

  return (
    <Tooltip tooltip="Flip Legs" targetStyle={{ width: '24px' }}>
      <IconButton
        icon={IconName.Repeat}
        onClick={handleSwapLegs}
        disabled={isLoading}
        data-testid="multileg-swap-legs-button"
        size={FormControlSizes.Small}
      />
    </Tooltip>
  );
}
